import Papa from 'papaparse';
import {
    BidAskDataPoint,
    FairValueDataPoint,
    TradeDataPoint,
    MarketAnalysisData,
    CandleDataPoint,
    CandleMarketInfo,
} from './types';

// Raw CSV row types (as parsed from CSV)
interface BidAskCsvRow {
    series: string;
    name: string;
    source: string;
    entity: string;
    type: string;
    period: string;
    data: string; // JSON string
    timestamp: string; // nanoseconds
}

interface FairValueTargetCsvRow {
    series: string;
    name: string;
    source: string;
    entity: string;
    type: string;
    period: string;
    data: string; // JSON string
    timestamp: string; // nanoseconds
}

interface TradeCsvRow {
    transaction_id: string;
    account_id: string;
    location: string;
    status: string;
    liquidity_source: string;
    side: string;
    base_asset: string;
    quote_asset: string;
    price: string;
    base_amount: string;
    quote_amount: string;
    timestamp: string; // nanoseconds
}

interface CandleCsvRow {
    series: string;
    name: string;
    source: string;
    entity: string;
    type: string;
    period: string;
    data: string; // JSON string
    timestamp: string; // nanoseconds
}

// Parsed JSON structures from data field
interface BidAskData {
    bid_price: number;
    ask_price: number;
    bid_amount: number;
    ask_amount: number;
}

interface FairValueData {
    probability: number;
    uncertainty: number;
    market_info?: {
        question?: string;
        end_date?: string;
    };
}

interface CandleData {
    open: number;
    high: number;
    low: number;
    close: number;
    base_volume: number;
    quote_volume: number;
    trade_count: number;
    vwap?: number;
    open_timestamp: string;
    close_timestamp: string;
    market_info?: {
        outcome: string;
        end_date: string;
        question: string;
        start_date: string;
        event_title: string;
        condition_id: string;
        market_duration: [number, number];
        event_start_time: string;
    };
}

/**
 * Convert nanoseconds timestamp string to Date
 * Timestamps are 19 digits like 1764228780000000000
 */
function nanosToDate(nanosStr: string): Date {
    // Use BigInt for precision with 19-digit numbers
    const nanos = BigInt(nanosStr);
    const millis = Number(nanos / BigInt(1_000_000));
    return new Date(millis);
}

/**
 * Options for streaming CSV parsing
 */
interface StreamingOptions<TRaw, TTransformed> {
    url: string;
    transform: (row: TRaw) => TTransformed | null;
    filter?: (row: TTransformed) => boolean;
}

/**
 * Fetch and parse CSV file using streaming to reduce memory usage.
 * Transforms rows during parsing rather than loading entire file first.
 */
async function fetchCsvStreaming<TRaw, TTransformed>(
    options: StreamingOptions<TRaw, TTransformed>
): Promise<TTransformed[]> {
    const { url, transform, filter } = options;
    const results: TTransformed[] = [];

    return new Promise((resolve, reject) => {
        Papa.parse<TRaw>(url, {
            download: true,
            header: true,
            skipEmptyLines: true,
            dynamicTyping: false, // Keep as strings for precision
            chunk: chunk => {
                for (const row of chunk.data) {
                    try {
                        const transformed = transform(row);
                        if (
                            transformed !== null &&
                            (!filter || filter(transformed))
                        ) {
                            results.push(transformed);
                        }
                    } catch (error) {
                        console.warn('[csvDataLoader] Failed to transform row:', {
                            error: error instanceof Error ? error.message : String(error),
                            row,
                        });
                    }
                }
            },
            complete: () => resolve(results),
            error: error =>
                reject(new Error(`Failed to parse ${url}: ${error.message}`)),
        });
    });
}

/**
 * Transform a single bid_ask CSV row to typed data point
 */
function transformBidAskRow(row: BidAskCsvRow): BidAskDataPoint | null {
    try {
        const data: BidAskData = JSON.parse(row.data);
        return {
            timestamp: nanosToDate(row.timestamp),
            bidPrice: data.bid_price,
            askPrice: data.ask_price,
            bidAmount: data.bid_amount,
            askAmount: data.ask_amount,
        };
    } catch (error) {
        console.warn('[csvDataLoader] Failed to parse bid/ask row:', {
            error: error instanceof Error ? error.message : String(error),
            timestamp: row.timestamp,
            data: row.data?.substring(0, 100),
        });
        return null;
    }
}

/**
 * Transform a single fair_value_target CSV row to typed data point
 */
function transformFairValueRow(
    row: FairValueTargetCsvRow
): FairValueDataPoint | null {
    try {
        const data: FairValueData = JSON.parse(row.data);
        const probability = data.probability;
        const uncertainty = data.uncertainty;
        return {
            timestamp: nanosToDate(row.timestamp),
            probability,
            uncertainty,
            upperBound: Math.min(1, probability + uncertainty),
            lowerBound: Math.max(0, probability - uncertainty),
        };
    } catch (error) {
        console.warn('[csvDataLoader] Failed to parse fair value row:', {
            error: error instanceof Error ? error.message : String(error),
            timestamp: row.timestamp,
            data: row.data?.substring(0, 100),
        });
        return null;
    }
}

/**
 * Transform a single trade CSV row to typed data point
 */
function transformTradeRow(row: TradeCsvRow): TradeDataPoint | null {
    try {
        const side = row.side;
        if (side !== 'buy' && side !== 'sell') {
            console.warn('[csvDataLoader] Invalid trade side:', {
                side,
                transactionId: row.transaction_id,
            });
            return null;
        }
        return {
            timestamp: nanosToDate(row.timestamp),
            price: parseFloat(row.price),
            side,
            amount: parseFloat(row.base_amount),
            transactionId: row.transaction_id,
        };
    } catch (error) {
        console.warn('[csvDataLoader] Failed to parse trade row:', {
            error: error instanceof Error ? error.message : String(error),
            transactionId: row.transaction_id,
            timestamp: row.timestamp,
        });
        return null;
    }
}

/**
 * Transform a single candle CSV row to typed data point
 */
function transformCandleRow(row: CandleCsvRow): CandleDataPoint | null {
    try {
        const data: CandleData = JSON.parse(row.data);
        const marketInfo: CandleMarketInfo | undefined = data.market_info
            ? {
                  outcome: data.market_info.outcome,
                  endDate: data.market_info.end_date,
                  question: data.market_info.question,
                  startDate: data.market_info.start_date,
                  eventTitle: data.market_info.event_title,
                  conditionId: data.market_info.condition_id,
                  marketDuration: data.market_info.market_duration,
                  eventStartTime: data.market_info.event_start_time,
              }
            : undefined;

        return {
            timestamp: nanosToDate(row.timestamp),
            open: data.open,
            high: data.high,
            low: data.low,
            close: data.close,
            baseVolume: data.base_volume,
            quoteVolume: data.quote_volume,
            tradeCount: data.trade_count,
            vwap: data.vwap ?? data.close,
            openTimestamp: new Date(data.open_timestamp),
            closeTimestamp: new Date(data.close_timestamp),
            marketInfo,
            isFilled: false,
        };
    } catch (error) {
        console.warn('[csvDataLoader] Failed to parse candle row:', {
            error: error instanceof Error ? error.message : String(error),
            timestamp: row.timestamp,
            data: row.data?.substring(0, 100),
        });
        return null;
    }
}

/**
 * Sort array by timestamp ascending
 */
function sortByTimestamp<T extends { timestamp: Date }>(arr: T[]): T[] {
    return arr.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}

/**
 * Load all market analysis data from CSV files using streaming
 * @param basePath - Path to the folder containing CSV files (e.g., '/data/polymarket/full_series')
 * @param includeCandles - Whether to also load candles.csv (default: false)
 */
export async function loadMarketAnalysisData(
    basePath: string,
    includeCandles = false
): Promise<MarketAnalysisData> {
    const promises: Promise<unknown>[] = [
        fetchCsvStreaming({
            url: `${basePath}/bid_ask.csv`,
            transform: transformBidAskRow,
        }),
        fetchCsvStreaming({
            url: `${basePath}/fair_value_target.csv`,
            transform: transformFairValueRow,
        }),
        fetchCsvStreaming({
            url: `${basePath}/trades.csv`,
            transform: transformTradeRow,
        }),
    ];

    if (includeCandles) {
        promises.push(
            fetchCsvStreaming({
                url: `${basePath}/candles.csv`,
                transform: transformCandleRow,
            })
        );
    }

    const results = await Promise.all(promises);

    return {
        bidAsk: sortByTimestamp(results[0] as BidAskDataPoint[]),
        fairValue: sortByTimestamp(results[1] as FairValueDataPoint[]),
        trades: sortByTimestamp(results[2] as TradeDataPoint[]),
        candles: includeCandles
            ? sortByTimestamp(results[3] as CandleDataPoint[])
            : undefined,
    };
}

/**
 * Load candle data from CSV file using streaming
 * @param basePath - Path to the folder containing candles.csv
 */
export async function loadCandleData(
    basePath: string
): Promise<CandleDataPoint[]> {
    const candles = await fetchCsvStreaming({
        url: `${basePath}/candles.csv`,
        transform: transformCandleRow,
    });
    return sortByTimestamp(candles);
}

/**
 * Filter data points by date range
 */
export function filterByDateRange<T extends { timestamp: Date }>(
    data: T[],
    fromDate: Date,
    toDate: Date
): T[] {
    const from = fromDate.getTime();
    const to = toDate.getTime();
    return data.filter(d => {
        const ts = d.timestamp.getTime();
        return ts >= from && ts <= to;
    });
}

/**
 * Get the date range of the data
 */
export function getDataDateRange(data: MarketAnalysisData): {
    minDate: Date | null;
    maxDate: Date | null;
} {
    const allTimestamps: Date[] = [
        ...data.bidAsk.map(d => d.timestamp),
        ...data.fairValue.map(d => d.timestamp),
        ...data.trades.map(d => d.timestamp),
        ...(data.candles?.map(d => d.timestamp) ?? []),
    ];

    if (allTimestamps.length === 0) {
        return { minDate: null, maxDate: null };
    }

    const times = allTimestamps.map(d => d.getTime());
    return {
        minDate: new Date(Math.min(...times)),
        maxDate: new Date(Math.max(...times)),
    };
}
